import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Spinner } from '../ui';
import TicketSelector from './TicketSelector';
import SkillQuestion from './SkillQuestion';
import { OddsComparison } from './OddsDisplay';
import { useSkillQuestion } from '../../hooks/useSkillQuestion';
import { useUserEntries } from '../../hooks/useUserEntries';
import { purchaseEntry, validateSkillAnswer } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';

/**
 * EntryModal - Lucky Number Design System
 *
 * Multi-step modal for raffle entry:
 * 1. Select ticket quantity
 * 2. Answer skill question
 * 3. Redirect to Stripe Checkout
 *
 * Features cream background with accent processing spinner.
 *
 * Security Notes:
 * - Skill answer validated SERVER-SIDE
 * - Price calculated SERVER-SIDE (not trusted from client)
 * - Checkout session created by Cloud Function
 */

const STEPS = {
  TICKETS: 'tickets',
  QUESTION: 'question',
  PROCESSING: 'processing',
};

export default function EntryModal({
  isOpen,
  onClose,
  raffle,
}) {
  const navigate = useNavigate();

  // State
  const [step, setStep] = useState(STEPS.TICKETS);
  const [ticketCount, setTicketCount] = useState(1);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Hooks
  const { question, loading: questionLoading, error: questionError, refreshQuestion } = useSkillQuestion();
  const { totalTickets: userExistingTickets } = useUserEntries(raffle?.id);

  // Raffle data
  const {
    id: raffleId,
    title,
    ticketPrice,
    ticketQuantity,
    ticketsSold = 0,
  } = raffle || {};

  const ticketsRemaining = ticketQuantity - ticketsSold;

  // Entry limits: Max 20% of total tickets per user, capped at 10
  const MAX_TICKETS_ABSOLUTE = 10;
  const MAX_PERCENT_OF_TOTAL = 0.20;
  const maxTicketsAllowed = Math.min(
    MAX_TICKETS_ABSOLUTE,
    Math.floor(ticketQuantity * MAX_PERCENT_OF_TOTAL)
  );
  const userRemainingAllowance = Math.max(0, maxTicketsAllowed - userExistingTickets);

  // Effective max is the minimum of remaining allowance and remaining tickets
  const effectiveMaxTickets = Math.min(userRemainingAllowance, ticketsRemaining);

  // Reset state when modal opens/closes
  const handleClose = useCallback(() => {
    setStep(STEPS.TICKETS);
    setTicketCount(1);
    setError(null);
    setProcessing(false);
    onClose();
  }, [onClose]);

  // Handle ticket selection
  const handleTicketChange = (count) => {
    setTicketCount(count);
  };

  // Proceed to question step
  const handleContinueToQuestion = () => {
    setError(null);
    setStep(STEPS.QUESTION);
  };

  // Handle skill answer submission
  const handleAnswerSubmit = async (answerIndex) => {
    if (!question) return false;

    setError(null);

    try {
      // First validate the answer (optional but provides faster feedback)
      const validation = await validateSkillAnswer(question.id, answerIndex);

      if (!validation.correct) {
        // Refresh question for retry
        refreshQuestion();
        return false; // Indicates incorrect answer to SkillQuestion component
      }

      // Answer correct - proceed to payment
      setStep(STEPS.PROCESSING);
      setProcessing(true);

      // Create checkout session
      const result = await purchaseEntry({
        raffleId,
        ticketCount,
        skillQuestionId: question.id,
        skillAnswer: answerIndex,
      });

      if (result.success && result.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkoutUrl;
      } else {
        throw new Error(result.error || 'Failed to create payment session');
      }

      return true; // Indicates correct answer
    } catch (err) {
      console.error('Entry error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setStep(STEPS.QUESTION);
      setProcessing(false);

      // If it's an answer validation error, return false
      if (err.code === 'SKILL_ANSWER_WRONG') {
        refreshQuestion();
        return false;
      }

      return true; // Other errors don't mean wrong answer
    }
  };

  // Go back to ticket selection
  const handleBackToTickets = () => {
    setStep(STEPS.TICKETS);
    setError(null);
  };

  if (!raffle) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === STEPS.TICKETS ? 'Enter Competition' : step === STEPS.QUESTION ? 'Answer to Continue' : 'Processing...'}
      size="md"
      closeOnOverlayClick={!processing}
      closeOnEscape={!processing}
      showCloseButton={!processing}
    >
      {/* Error display */}
      {error && (
        <div className="mb-4 p-4 bg-error/10 rounded-xl border border-error/30">
          <p className="text-sm text-error font-medium">{error}</p>
        </div>
      )}

      {/* Step 1: Ticket Selection */}
      {step === STEPS.TICKETS && (
        <div className="space-y-6">
          {/* Raffle info */}
          <div className="flex items-center gap-4 pb-4 border-b border-midnight/10">
            {raffle.photos?.[0] && (
              <img
                src={raffle.photos[0]}
                alt={title}
                className="w-16 h-16 rounded-xl object-cover ring-2 ring-midnight/10"
              />
            )}
            <div>
              <h4 className="font-display font-semibold text-midnight line-clamp-2">{title}</h4>
              <p className="text-sm text-midnight/60">
                {formatCurrency(ticketPrice)} per entry
              </p>
            </div>
          </div>

          {/* Ticket selector */}
          <TicketSelector
            ticketPrice={ticketPrice}
            ticketsRemaining={effectiveMaxTickets}
            initialCount={ticketCount}
            onChange={handleTicketChange}
            light
          />

          {/* Show limit info if user has existing entries */}
          {userExistingTickets > 0 && userRemainingAllowance < maxTicketsAllowed && (
            <p className="text-xs text-midnight/60 text-center -mt-2">
              You can purchase up to {userRemainingAllowance} more {userRemainingAllowance !== 1 ? 'entries' : 'entry'} (max {maxTicketsAllowed} per person)
            </p>
          )}

          {/* Odds preview */}
          {userExistingTickets > 0 && (
            <OddsComparison
              currentTickets={userExistingTickets}
              newTickets={ticketCount}
              totalTickets={ticketsSold}
              light
            />
          )}

          {/* Continue button */}
          <Button fullWidth onClick={handleContinueToQuestion}>
            Continue
          </Button>

          {/* Info text */}
          <p className="text-xs text-midnight/50 text-center">
            You'll need to answer a skill question to complete your entry.
          </p>
        </div>
      )}

      {/* Step 2: Skill Question */}
      {step === STEPS.QUESTION && (
        <div className="space-y-4">
          <SkillQuestion
            question={question}
            loading={questionLoading}
            error={questionError}
            onAnswer={handleAnswerSubmit}
            disabled={processing}
            light
          />

          {/* Back button */}
          <button
            type="button"
            onClick={handleBackToTickets}
            disabled={processing}
            className="w-full text-center text-sm text-midnight/50 hover:text-midnight transition-colors disabled:opacity-50"
          >
            Back to entry selection
          </button>
        </div>
      )}

      {/* Step 3: Processing */}
      {step === STEPS.PROCESSING && (
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center">
            <Spinner size="lg" variant="accent" />
          </div>
          <p className="text-midnight font-display font-semibold">Creating your entry...</p>
          <p className="text-sm text-midnight/60 mt-1">
            You'll be redirected to payment shortly.
          </p>
        </div>
      )}
    </Modal>
  );
}

/**
 * useEntryModal
 *
 * Hook to manage entry modal state.
 */
export function useEntryModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [raffle, setRaffle] = useState(null);

  const openModal = useCallback((raffleData) => {
    setRaffle(raffleData);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    // Keep raffle data briefly for exit animation
    setTimeout(() => setRaffle(null), 300);
  }, []);

  return {
    isOpen,
    raffle,
    openModal,
    closeModal,
  };
}
